import React, { useEffect, useRef, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon, Upload, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { LockedModule } from "./SchoolAnalytics";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const ACCEPTED = "image/webp,image/jpeg,image/jpg,image/png";
const MAX_FILES = 12;
const MAX_MB = 10;

// Resolve a stored path to a full URL — new uploads are stored as `/api/v1/uploads/...`
// while some legacy schools may still have absolute http(s) URLs in the gallery.
const resolveUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u || ""}`);

export default function SchoolGallery() {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const mod = modules.find((m) => m.key === "school_gallery");
  const owned = mod?.owned;

  const load = async () => {
    const [m, s] = await Promise.all([
      api.get(`${V1}/school-modules`),
      user?.school_id ? api.get(`${V1}/schools/${user.school_id}`) : Promise.resolve({ data: {} }),
    ]);
    setModules(m.data || []);
    setGallery(s.data?.gallery || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.school_id]);

  const onPick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > MAX_FILES) {
      toast.error(`Tek seferde en fazla ${MAX_FILES} dosya seçebilirsiniz`);
      return;
    }
    if (files.some((f) => f.size > MAX_MB * 1024 * 1024)) {
      toast.error(`Her dosya en fazla ${MAX_MB} MB olmalı`);
      return;
    }
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const { data } = await api.post(`${V1}/school/media/gallery/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setGallery(data.gallery || []);
      toast.success(`${data.uploaded?.length || 0} görsel yüklendi ve 1024×700 WebP formatına çevrildi`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (url) => {
    if (!window.confirm("Bu görseli kaldırmak istiyor musun?")) return;
    try {
      const { data } = await api.delete(`${V1}/school/media/gallery/item`, { params: { url } });
      setGallery(data.gallery || []);
      toast.success("Görsel kaldırıldı");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinemedi");
    }
  };

  if (!mod) return <div className="text-muted-foreground">Yükleniyor…</div>;
  if (!owned) return <LockedModule mod={mod} testid="gallery-locked" />;

  return (
    <div className="space-y-6" data-testid="school-gallery-page">
      <div>
        <div className="overline text-accent">Modül · {mod.label}</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><ImageIcon className="h-7 w-7 text-primary" /> Okul Galerisi</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Kampüs/etkinlik/sınıf fotoğraflarını doğrudan yükle. Sistem her görseli otomatik olarak
          <b> 1024×700</b> boyutuna kırpar ve WebP formatına dönüştürür. Kabul edilenler: WebP, JPG, PNG.
          Maksimum {MAX_FILES} dosya, her biri {MAX_MB}MB.
        </p>
      </div>

      <div className="p-6 rounded-xl border-2 border-dashed border-border bg-card flex flex-col items-center gap-3 text-center">
        <ImageIcon className="h-10 w-10 text-muted-foreground" />
        <div className="font-medium">Fotoğrafları buradan yükle</div>
        <div className="text-xs text-muted-foreground">Toplu seçim yapabilirsin — sistem otomatik kırpar ve isim verir.</div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          multiple
          className="hidden"
          onChange={onFiles}
          data-testid="gallery-file-input"
        />
        <Button onClick={onPick} disabled={uploading} data-testid="gallery-upload-btn">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Yükleniyor…" : "Dosya Seç ve Yükle"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {gallery.length === 0 && (
          <div className="col-span-2 md:col-span-4 p-10 rounded-xl border border-dashed border-border text-center text-muted-foreground">
            Henüz görsel yok.
          </div>
        )}
        {gallery.map((url, i) => (
          <div key={url + i} className="relative group rounded-xl border border-border overflow-hidden bg-card aspect-[1024/700]" data-testid={`gallery-item-${i}`}>
            <img
              src={resolveUrl(url)}
              alt={`Galeri ${i + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.target.style.opacity = 0.3; }}
            />
            <button
              className="absolute top-2 right-2 p-1.5 rounded bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition"
              onClick={() => remove(url)}
              data-testid={`gallery-remove-${i}`}
              title="Kaldır"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="text-xs text-muted-foreground">
        Toplam görsel: <b>{gallery.length}</b> / 30
      </div>
    </div>
  );
}
